<template>
  <div class="sc-window">

    <!-- Header -->
    <div class="sc-header">
      <div class="sc-title">
        <i class="bi bi-chat-dots-fill"></i> SmartChat
        <span v-if="activeUser" class="sc-inbox-label">— {{ activeUser.name }}</span>
        <span v-else class="sc-inbox-label">— Team Inbox</span>
      </div>
      <div class="sc-controls">

        <div class="dropdown" :class="{ show: statusOpen }">
          <button
            class="btn sc-status-btn dropdown-toggle"
            :class="statusClass"
            type="button"
            @click="statusOpen = !statusOpen"
          >
            {{ status }}
          </button>
          <ul class="dropdown-menu dropdown-menu-end" :class="{ show: statusOpen }">
            <li v-for="opt in statusOptions" :key="opt.label">
              <a class="dropdown-item" href="#" @click.prevent="setStatus(opt)">
                <span class="sc-dot" :style="{ background: opt.color }"></span> {{ opt.label }}
              </a>
            </li>
          </ul>
        </div>

        <!--<button type="button" class="btn sc-logoff-btn" title="Log Off" @click="$emit('logoff')">
          <i class="bi bi-box-arrow-right"></i> Log Off
        </button>-->

        <button
          type="button"
          class="sc-icon-btn"
          :class="{ active: usersOpen }"
          title="Show users"
          @click="usersOpen = !usersOpen"
        >
          <i class="bi bi-people"></i>
        </button>        

        <button type="button" class="sc-icon-btn" title="Minimize">&#95;</button>
        <button type="button" class="sc-icon-btn" title="Close"><i class="bi bi-x-lg"></i></button>
      </div>
    </div>

    <!-- Body -->
    <div class="sc-body">

      <div class="sc-chat">
        <div v-if="currentMessages.length === 0" class="sc-empty">
          No messages yet with {{ activeUser ? activeUser.name : 'this inbox' }}.
        </div>
        <div v-for="(msg, i) in currentMessages" :key="i" class="sc-msg">
          <img class="sc-avatar" :src="msg.avatar" alt="">
          <div class="sc-msg-body">
            <span class="sc-name">{{ msg.name }}</span>
            <p class="sc-text">{{ msg.text }}</p>
            <div v-for="(file, j) in msg.attachments" :key="j" class="sc-attachment">
              <i :class="file.icon"></i> {{ file.name }}
            </div>
          </div>
        </div>
      </div>

      <!-- Users panel -->
      <div class="sc-users-panel" :class="{ open: usersOpen }">
        <div class="sc-users-list">
          <div
            v-for="user in filteredUsers"
            :key="user.name"
            class="sc-user-item"
            :class="{ active: activeUser && activeUser.name === user.name }"
            @click="selectInbox(user); usersOpen = false"
          >
            <img v-if="user.avatar" class="sc-user-avatar" :src="user.avatar" alt="">
            <span v-else class="sc-user-avatar"><i class="bi bi-person-fill"></i></span>
            <div class="sc-user-name">{{ user.name }}</div>
            <div class="sc-user-meta">
              <span v-if="user.badge" class="badge rounded-pill bg-secondary">{{ user.badge }}</span>
              <span class="sc-status-dot" :class="'status-' + user.status"></span>
              <span v-if="user.status !== 'online' && user.lastSeen">{{ user.lastSeen }}</span>
            </div>
          </div>
        </div>
        <div class="sc-users-filter">
          <input
            type="text"
            class="form-control form-control-sm"
            placeholder="Filter"
            v-model="userFilter"
          >
        </div>
      </div>

    </div>

    <!-- Footer -->
    <div class="sc-footer">
      <textarea
        class="form-control"
        rows="2"
        placeholder="Write a reply..."
        v-model="draft"
        @keydown.enter="onEnter"
      ></textarea>
      <div class="sc-footer-actions">
        <button type="button" class="sc-camera-btn" title="Attach photo"><i class="bi bi-camera-fill"></i></button>
        <div class="d-flex align-items-center gap-3">
          <label class="sc-enter-check">
            <input type="checkbox" class="form-check-input mt-0" v-model="sendOnEnter"> Press ENTER to send
          </label>
          <button type="button" class="btn btn-primary sc-reply-btn" @click="sendReply">Reply</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'SmartChat',
  emits: ['logoff', 'send', 'inbox-change'],
  data() {
    return {
      statusOpen: false,
      usersOpen: false,
      inboxOpen: false,
      status: 'Online',
      statusClass: 'status-online',
      statusOptions: [
        { label: 'Online', cls: 'status-online', color: '#5cb85c' },
        { label: 'Busy', cls: 'status-busy', color: '#d9534f' },
        { label: 'Away', cls: 'status-away', color: '#f0ad4e' },
        { label: 'Log Off', cls: 'status-logoff', color: '#f0ad4e' },
      ],
      draft: '',
      sendOnEnter: false,
      userFilter: '',
      activeUser: null, // null = Team Inbox

      // Default "Team Inbox" conversation (shown when no user is selected)
      teamMessages: [
        {
          name: 'Sadi Orlaf',
          avatar: 'https://i.pravatar.cc/80?img=47',
          text: "Hey did you meet the new boss yet? Trick question, you already work with him! But you ask me...anyway here is the report you needed before the product launch with Lisa and Andrew, you can check it whenever you want.",
          attachments: [
            { name: 'report-2013-demographic-review.pdf', icon: 'bi bi-file-earmark-text' },
            { name: 'tobacco-report-2012.doc', icon: 'bi bi-file-earmark-word' },
          ],
        },
        {
          name: 'John Doe',
          avatar: 'https://i.pravatar.cc/80?img=12',
          text: "Haha! Yeah I know what you mean, he can be a bit intense sometimes but he means well. Thanks for sending those over, I'll take a look this afternoon.",
          attachments: [],
        },
      ],

      // Per-user conversations, keyed by user name. Switching inbox shows this list instead.
      conversations: {
        'Robin Berry': [
          { name: 'Robin Berry', avatar: 'https://i.pravatar.cc/80?img=47', text: 'Can you review the demographic report before EOD?', attachments: [] },
        ],
        'Sadi Orlaf': [
          { name: 'Sadi Orlaf', avatar: 'https://i.pravatar.cc/80?img=32', text: 'Sent you the launch docs, let me know if anything is missing.', attachments: [
            { name: 'report-2013-demographic-review.pdf', icon: 'bi bi-file-earmark-text' },
          ] },
        ],
        'John Doe': [
          { name: 'John Doe', avatar: 'https://i.pravatar.cc/80?img=12', text: "I'll take a look this afternoon.", attachments: [] },
        ],
      },

      users: [
        { name: 'Robin Berry', avatar: 'https://i.pravatar.cc/64?img=47', badge: 23, status: 'online' },
        { name: 'Mark Zeukartech', status: 'offline', lastSeen: '2hrs' },
        { name: 'Belmain Dolson', status: 'offline', lastSeen: '45m' },
        { name: 'Galvitch Drewbery', status: 'online' },
        { name: 'Sadi Orlaf', avatar: 'https://i.pravatar.cc/64?img=32', status: 'online' },
        { name: 'John Doe', status: 'away', lastSeen: '1d' },
        { name: 'Priya Anand', status: 'online' },
        { name: 'Chen Wei', status: 'away', lastSeen: '3d' },
        { name: 'Ola Nordman', status: 'online' },
        { name: 'Farah Hassan', status: 'offline', lastSeen: '6d' },
      ],
    };
  },
  computed: {
    filteredUsers() {
      const q = this.userFilter.trim().toLowerCase();
      if (!q) return this.users;
      return this.users.filter(u => u.name.toLowerCase().includes(q));
    },
    currentMessages() {
      if (!this.activeUser) return this.teamMessages;
      return this.conversations[this.activeUser.name] || [];
    },
  },
  methods: {
    setStatus(opt) {
      this.status = opt.label;
      this.statusClass = opt.cls;
      this.statusOpen = false;
    },
    selectInbox(user) {
      this.activeUser = user;
      this.inboxOpen = false;
      this.$emit('inbox-change', user);
    },
    onEnter(e) {
      if (this.sendOnEnter && !e.shiftKey) {
        e.preventDefault();
        this.sendReply();
      }
    },
    sendReply() {
      if (!this.draft.trim()) return;
      this.$emit('send', { to: this.activeUser, text: this.draft });
      this.draft = '';
    },
  },
};
</script>